import React, { useState } from 'react';
import * as yup from 'yup';
import ImageUpload from '../../forms/ImageUpload';
import FormField from '../../forms/FormField/FormField';
import FormContainer, { styles } from '../../forms/FormContainer/FormContainer';
import SubmitButton from '../../forms/SubmitButton/SubmitButton';
import { useFormik } from 'formik';
import submitForm from '../../../services/submitMarketplaceApplication';

interface userData {
    address: {
        line_1: string,
        line_2: string,
        city: string,
        postcode: string
    },
    phone: string,
    profile_bio?: string,
    profile_name?: string,
    profile_id?: string,
    profile_image?: boolean,
    hero_image?: boolean,
    membership_level?: string,
}
declare const userData: userData;

const getInitialMembershipLevel = () => {
    let level = '';
    if (userData.membership_level && ['premium', 'pro'].includes(userData.membership_level)) {
        level = userData.membership_level;
    }
    const params = new URLSearchParams(window.location.search);
    const queryLevel = params.get('level');
    if (queryLevel === 'pro' || queryLevel === 'premium') {
        level = queryLevel;
    }
    return level;
};

const validationSchema = yup.object({
    name: yup.string().required('Seller profile name is required.'),
    bio: yup.string().required('Seller bio is required.'),
    address: yup.string().required('Seller address is required.'),
    phone: yup.string().required('Seller phone number is required.'),
    membershipLevel: yup.string().oneOf(['premium', 'pro'], 'Please select a membership level.').required('Please select a membership level.'),
    logo: yup.array()
        .min(1, 'Please upload an image.')
        .test('fileSize', 'Image must be less than 8MB.', value => {
            let valid = true;
            value?.forEach(file => {
                if (file.size > 8 * 1024 * 1024) {
                    valid = false;
                }
            });
            return valid;
        })
        .test('fileType', 'Unsupported file type, only JPG, PNG and GIF are accepted.', value => {
            const supportedTypes = ['image/png', 'image/jpeg', 'image/gif'];
            let valid = true;
            value?.forEach(file => {
                if (!supportedTypes.includes(file.type)) {
                    valid = false;
                }
            });
            return valid;
        }),
    hero: yup.array()
        .min(1, 'Please upload an image.')
        .test('fileSize', 'Image must be less than 8MB.', value => {
            let valid = true;
            value?.forEach(file => {
                if (file.size > 8 * 1024 * 1024) {
                    valid = false;
                }
            });
            return valid;
        })
        .test('fileType', 'Unsupported file type, only JPG, PNG and GIF are accepted.', value => {
            const supportedTypes = ['image/png', 'image/jpeg', 'image/gif'];
            let valid = true;
            value?.forEach(file => {
                if (!supportedTypes.includes(file.type)) {
                    valid = false;
                }
            });
            return valid;
        }),
});

function formatAddress(address) {
    const { line_1, line_2, city, postcode } = address;
    const parts = [line_1, line_2, city, postcode];
    return parts.filter(part => part).join(', ');  // Filters out any undefined or null parts and then joins the rest.
};

export default function Form({ setJourney, setProfileId }) {
    const [loading, setLoading] = useState(false);
    const formik = useFormik({
        initialValues: {
            name: userData.profile_name ? userData.profile_name : '',
            bio: userData.profile_bio ? userData.profile_bio : '',
            address: formatAddress(userData.address),
            phone: userData.phone,
            membershipLevel: getInitialMembershipLevel(),
            logo: [],
            hero: []
        },
        validationSchema: validationSchema,
        onSubmit: values => submitForm(values, setJourney, setLoading, setProfileId),
    });
    return (
        <FormContainer>
            <form className={loading ? styles.loading : ""} onSubmit={formik.handleSubmit}>
                <FormField label="Seller name:" type="text" name="name" formik={formik} />
                <FormField label="Seller bio:" type="textarea" name="bio" formik={formik} />
                <FormField label="Address:" type="text" name="address" formik={formik} />
                <FormField label="Phone number:" type="text" name="phone" formik={formik} />
                <FormField
                    label="Membership Level:"
                    type="select"
                    name="membershipLevel"
                    formik={formik}
                    options={{
                        '': 'Please select...',
                        'premium': 'Premium - £75',
                        'pro': 'Pro - £150'
                    }}
                />
                <ImageUpload formik={formik} singleImage={true} label="Upload your logo" dropzoneText="Drop your image here or click to select." fieldName="logo" />
                <ImageUpload formik={formik} singleImage={true} label="Upload your profile background" dropzoneText="Drop your image here or click to select." fieldName="hero" />
                {!userData.profile_id && (<SubmitButton formik={formik} text='Create my profile' />)}
                {userData.profile_id && (<SubmitButton formik={formik} text='Update my profile' />)}
                <p>By signing up for a Meatex Marketplace account you are agreeing to our <a target="_blank" href="/marketplace-terms-and-conditions/">Marketplace Terms & Conditions</a>, and consent to your data being processed in line with our <a target="_blank" href="/privacy-policy/">Privacy Policy</a>.</p>
            </form>
        </FormContainer>
    )
}